import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, finalize, map, switchMap, take, tap } from 'rxjs/operators';
import { ROLE_SAV } from 'src/app/shared/constants/authorities.constants';
import { Environment } from '../environment';
import { isServerUnavailableError } from '../http/http-error.service';
import { NotificationService } from '../notification/notification.service';
import { AuthenticationStore } from './authentication.store';
import { User } from './user.model';

@Injectable()
export class AuthenticationService {
    adLoginPath = `${Environment.host}/oauth2/authorization/adfs`;

    constructor(
        private http: HttpClient,
        private authenticationStore: AuthenticationStore,
        private notificationService: NotificationService,
    ) {}

    login(value: { email: string; password: string; rememberMe?: boolean }): Observable<User> {
        return this.http
            .post(`${Environment.apiUrl}/authenticate`, {
                username: value.email.trim(),
                password: value.password,
                rememberMe: value.rememberMe == true,
            })
            .pipe(switchMap(() => this.queryAndSetCurrentUser(true)));
    }

    logout(): Observable<void> {
        localStorage.removeItem('userId');
        localStorage.setItem('logoutAsked', 'true');
        return this.http.post<void>(`${Environment.apiUrl}/logout`, {}).pipe(
            // On réinitialise le user, que l'appel ait réussi ou échoué (hors ligne)
            finalize(() => this.unsetCurrentUser()),
        );
    }

    /**
     * Vérifie si l'utilisateur est authentifié, et renseigne l'utilisateur courant dans le store le cas échéant.
     */
    queryAndSetCurrentUser(fromLogin: boolean = false): Observable<User> {
        return this.http.get<User>(`${Environment.apiUrl}/account`).pipe(
            tap((user) => {
                if (fromLogin) {
                    localStorage.removeItem('logoutAsked');
                    localStorage.setItem('lastLogin', new Date().toUTCString());
                }
                localStorage.setItem('company', user.company);
                localStorage.setItem('userId', user.id);
                if (localStorage.getItem('logoutAsked') === 'true') {
                    throw new Error('Logout was asked');
                }
                this.authenticationStore.setCurrentUser(user);
            }),
            catchError((err) => {
                // En cas d'erreur (ex : token invalide), sauf service indisponible, on logout
                if (!isServerUnavailableError(err)) {
                    this.notificationService.dismiss(); // Enlève la notification d'erreur 401 dans ce cas
                    return this.logout().pipe(map(() => null));
                } else {
                    return of(null);
                }
            }),
        );
    }

    private unsetCurrentUser() {
        this.authenticationStore.unsetCurrentUser();
    }

    impersonate = (userId: string): Observable<User> =>
        this.http.post<User>(`${Environment.apiUrl}/impersonate`, { userId }).pipe(
            take(1),
            tap((user) => {
                this.authenticationStore.setRealUser(this.authenticationStore.getCurrentUserSnapshot());
                this.authenticationStore.setCurrentUser(user);
            }),
        );

    unimpersonate = (): Observable<User> =>
        this.http.post<User>(`${Environment.apiUrl}/unimpersonate`, null).pipe(
            take(1),
            tap((user) => {
                this.authenticationStore.setRealUser(null);
                this.authenticationStore.setCurrentUser(user);
            }),
        );

    estSav(user: User): boolean {
        return !!user?.authorities?.includes(ROLE_SAV);
    }
}
